import { useEffect, useState, type ReactNode } from 'react';
import { Spinner, makeStyles } from '@fluentui/react-components';
import { ApiClientError } from '../api/client.js';
import { enterDemoGuest, logout, me } from '../api/auth.js';
import { useAuthStore, type AuthStatus } from '../stores/authStore.js';
import { useUsageStore } from '../stores/usageStore.js';
import { usePricingStore } from '../stores/pricingStore.js';
import { useQuoteJobsStore } from '../stores/quoteJobsStore.js';
import { publicDemoActivation } from '../lib/publicDemo.js';
import { DEMO } from '../demo/index.js';
import { DemoLeadGate } from './DemoLeadGate.js';

const useStyles = makeStyles({
  loader: {
    minHeight: '100vh',
    display: 'grid',
    placeItems: 'center',
    gap: 'var(--space-l)',
  },
});

const DEMO_LEAD_SS = 'opus_demo_lead_ok';

/**
 * Al montar, intenta hidratar la sesión llamando a /auth/me.
 * En host demo: gate de lead (nombre + correo) antes de emitir sesión guest.
 */
export function AuthBootstrap({ children }: { children: ReactNode }) {
  const styles = useStyles();
  const { status, setSession, clearSession } = useAuthStore((s) => ({
    status: s.status,
    setSession: s.setSession,
    clearSession: s.clearSession,
  }));
  const [demoNeedsLead, setDemoNeedsLead] = useState(false);
  // El modo demostración se decide al abrir la app y no cambia mientras la
  // pestaña siga abierta. `session` = lo prendió alguien a mano con `?demo=1`
  // (entonces sí hay puerta de salida); `host` = es el sitio público de verdad.
  const [demoMode] = useState(() => publicDemoActivation());
  const publicDemo = demoMode !== 'off';

  useEffect(() => {
    // Mockup estático (DEMO OPUS ai): la sesión de visitante ya se sembró en
    // main.tsx y no hay backend que capture leads — no aplica ningún gate.
    if (DEMO) return;
    let cancelled = false;
    (async () => {
      if (publicDemo) {
        try {
          // Si ya hay sesión guest válida y el lead se capturó en esta pestaña, reusar.
          let leadOk = false;
          try { leadOk = sessionStorage.getItem(DEMO_LEAD_SS) === '1'; } catch { /* ignore */ }
          if (leadOk) {
            try {
              const data = await me();
              if (!cancelled && data.demoGuest) {
                setSession(data);
                useUsageStore.getState().clear();
                usePricingStore.getState().clear();
                return;
              }
            } catch { /* caer al gate */ }
          }
          await logout().catch(() => undefined);
          if (!cancelled) {
            clearSession();
            setDemoNeedsLead(true);
          }
        } catch (demoErr) {
          console.error('demo bootstrap failed', demoErr);
          if (!cancelled) {
            clearSession();
            setDemoNeedsLead(true);
          }
        }
        return;
      }

      try {
        const data = await me();
        if (cancelled) return;
        // Sesión de visitante FUERA de la demostración: es una sesión que sobró
        // (el demo se probó en esta máquina y la cookie dura un día). Si se
        // adopta, la app entera se abre en modo demostración —sin proyectos
        // propios y sin "cerrar sesión"— y no queda forma de entrar con la
        // cuenta de uno. Se descarta y se pide iniciar sesión normal.
        if (data.demoGuest) {
          await logout().catch(() => undefined);
          if (cancelled) return;
          clearSession();
          useUsageStore.getState().clear();
          usePricingStore.getState().clear();
          return;
        }
        setSession(data);
        void useUsageStore.getState().refresh();
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiClientError && (err.status === 401 || err.status === 403)) {
          clearSession();
          useUsageStore.getState().clear();
          usePricingStore.getState().clear();
        } else {
          clearSession();
          useUsageStore.getState().clear();
          usePricingStore.getState().clear();
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [publicDemo, setSession, clearSession]);

  /**
   * En el sitio de demostración nadie entra con una cuenta, así que la pantalla
   * de inicio de sesión no le sirve a nadie: es un callejón sin salida. Si el
   * visitante se queda fuera por cualquier motivo —pasaron muchas horas, algo
   * falló— lo regresamos a la entrada de la demostración y sigue su recorrido.
   */
  useEffect(() => {
    if (!publicDemo || DEMO) return;
    if (status === 'unauthenticated') setDemoNeedsLead(true);
  }, [publicDemo, status]);

  useEffect(() => {
    const initial = useAuthStore.getState();
    let prevStatus: AuthStatus = initial.status;
    let prevTenantId: string | null = initial.tenant?.id ?? null;
    const unsub = useAuthStore.subscribe((state) => {
      const tenantId = state.tenant?.id ?? null;
      if (state.status === 'authenticated' && prevStatus !== 'authenticated') {
        void useUsageStore.getState().refresh();
      } else if (state.status === 'unauthenticated' && prevStatus === 'authenticated') {
        useUsageStore.getState().clear();
        usePricingStore.getState().clear();
        useQuoteJobsStore.getState().clear();
      } else if (state.status === 'authenticated' && tenantId !== prevTenantId) {
        // Cambio de empresa SIN cambio de estado de sesión (switch-tenant):
        // consumo, precios y avisos de cotización son de la empresa anterior.
        // Se limpian y se vuelve a cargar lo de la empresa nueva.
        useQuoteJobsStore.getState().clear();
        usePricingStore.getState().clear();
        useUsageStore.getState().clear();
        void useUsageStore.getState().refresh();
      }
      prevStatus = state.status;
      prevTenantId = tenantId;
    });
    return unsub;
  }, []);

  if (demoNeedsLead) {
    return (
      <DemoLeadGate
        canExit={demoMode === 'session'}
        onSubmit={async (lead) => {
          const guest = await enterDemoGuest(lead);
          try { sessionStorage.setItem(DEMO_LEAD_SS, '1'); } catch { /* ignore */ }
          setSession(guest);
          useUsageStore.getState().clear();
          usePricingStore.getState().clear();
          setDemoNeedsLead(false);
        }}
      />
    );
  }

  if (status === 'unknown') {
    return (
      <div className={styles.loader}>
        <Spinner label="Cargando sesión…" />
      </div>
    );
  }
  return <>{children}</>;
}
